/* eslint-disable @typescript-eslint/no-var-requires */
/**
 * Constructs a block of code and highlights it with highlight.js.
 *
 * @link https://highlightjs.org/
 */

import hljs from 'highlight.js/lib/highlight.js';
import stringify from 'json-stringify-pretty-compact';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './CodeBlock.module.scss';

hljs.registerLanguage('bash', require('highlight.js/lib/languages/bash'));
hljs.registerLanguage('http', require('highlight.js/lib/languages/http'));
hljs.registerLanguage('javascript', require('highlight.js/lib/languages/javascript'));
hljs.registerLanguage('json', require('highlight.js/lib/languages/json'));
hljs.registerLanguage('php', require('highlight.js/lib/languages/php'));
hljs.registerLanguage('python', require('highlight.js/lib/languages/python'));
hljs.registerLanguage('xml', require('highlight.js/lib/languages/xml'));

class CodeBlock extends React.Component {
  constructor(props) {
    super(props);

    this.highlightRef = null;

    this.setHighlightRef = (element) => {
      this.highlightRef = element;
    };

    this.highlightCode = () => {
      if (this.highlightRef !== null) {
        hljs.highlightBlock(this.highlightRef);
      }
    };
  }

  componentDidMount() {
    this.highlightCode();
  }

  render() {
    const paragraphTopMargin = { marginTop: '1.25rem' };
    let code = this.props.children;
    let endpoint = {
      contentType: this.props.endpoint.contentType || null,
      httpPrefix:
        typeof this.props.endpoint.httpPrefix !== 'undefined'
          ? this.props.endpoint.httpPrefix
          : true,
      leaveMethodCasingAlone:
        typeof this.props.endpoint.leaveMethodCasingAlone !== 'undefined'
          ? this.props.endpoint.leaveMethodCasingAlone
          : false,
      method: this.props.endpoint.method || null,
      params: this.props.endpoint.params || null,
      resource: this.props.endpoint.resource || null,
      resources: this.props.endpoint.resources || null,
      domain: this.props.endpoint.domain
        ? `https://${this.props.endpoint.domain}`
        : 'https://api.vimeo.com',
    };

    let language = this.props.language;
    let headers = this.props.headers;

    // Reformat all JSON examples so they look consistent.
    if (code && language.toLowerCase() === 'json') {
      code = stringify(code, {
        maxLength: 40,
        margins: true,
      });
    }

    let resource = null;
    let resources = null;
    if (endpoint.resource !== null || endpoint.resources !== null) {
      resource = endpoint.resource;
      resources = endpoint.resources;

      // If we're supplying an array of resources, but only have one, fallback to the singular design.
      if (Array.isArray(resources) && resources.length === 1) {
        resource = resources[0];
        resources = null;
      }
    }

    const hasResources = resource !== null || resources !== null;

    if (endpoint.method && !endpoint.leaveMethodCasingAlone) {
      endpoint.method = endpoint.method.toUpperCase();
    }

    return (
      <div>
        <pre>
          <code className="code-highlight">
            {hasResources && (
              <div>
                {resources ? (
                  <div className={styles.request_wrapper}>
                    {endpoint.method && (
                      <div className={styles.request_method_wrapper}>
                        <p>
                          <span className={styles.http_method}>{endpoint.method}</span>
                        </p>
                      </div>
                    )}

                    <div className={styles.resource_wrapper}>
                      {Object.keys(resources).map(function (i) {
                        return (
                          <p key={`resource-${i}`}>
                            {endpoint.httpPrefix && (
                              <span className={styles.white}>{endpoint.domain}</span>
                            )}
                            <span className={styles.orange}>{resources[i]}</span>
                            {endpoint.params && (
                              <span className={styles.orange}>{endpoint.params}</span>
                            )}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p>
                    {endpoint.method && (
                      <code className={styles.http_method}>{endpoint.method}</code>
                    )}
                    {endpoint.httpPrefix && <code className={styles.white}>{endpoint.domain}</code>}
                    <code className={styles.orange}>
                      {typeof resource !== 'undefined' ? resource : '/{endpoint}'}
                    </code>
                    {endpoint.params && <code className={styles.orange}>{endpoint.params}</code>}
                  </p>
                )}
              </div>
            )}

            {(endpoint.contentType || endpoint.payload || headers) && (
              <p className={styles.white} style={hasResources ? paragraphTopMargin : null}>
                {endpoint.contentType && <code>Content-Type: {endpoint.contentType}</code>}
                {endpoint.payload && <code className={styles.orange}>{endpoint.payload}</code>}

                {headers && (
                  <span>
                    {Object.keys(headers).map(function (i) {
                      return (
                        <span key={`header-${i}`}>
                          <code>{headers[i]}</code>
                          <br />
                        </span>
                      );
                    })}
                  </span>
                )}
              </p>
            )}

            {code && (
              <p
                className={styles.white}
                style={
                  hasResources || endpoint.contentType || endpoint.payload || headers
                    ? paragraphTopMargin
                    : null
                }
              >
                <code ref={this.setHighlightRef} className={'hljs ' + language}>
                  {code}
                </code>
              </p>
            )}
          </code>
        </pre>
      </div>
    );
  }
}

CodeBlock.defaultProps = {
  endpoint: {},
  language: 'bash',
};

CodeBlock.propTypes = {
  endpoint: PropTypes.shape({
    contentType: PropTypes.string,
    httpPrefix: PropTypes.bool,
    leaveMethodCasingAlone: PropTypes.bool,
    method: PropTypes.string,
    params: PropTypes.string,
    resource: PropTypes.string,
    resources: PropTypes.arrayOf(PropTypes.string),
    domain: PropTypes.string,
  }),
  language: PropTypes.string,
  headers: PropTypes.array,
};

export default CodeBlock;
